import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Package, Tag, Menu, X, LogOut, User, ChevronDown, Shield, Settings, Users, Boxes, List, Database, Layers } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from './ui/dropdown-menu';
import { Button } from './ui/button';
import HadenAtlasLogo from './HadenAtlasLogo';
import WhosOnline from './WhosOnline';
import PageViewers from './PageViewers';
import { usePresence } from '../contexts/PresenceContext';

const Layout = ({ children }) => {
  const location = useLocation();
  const [mobileMenuOpen, setMobileMenuOpen] = React.useState(false);
  const { user, logout, isAuthenticated } = useAuth();
  const { onlineUsers } = usePresence();

  // Derive self-presence: is the current user in the online list?
  const myId = user?.user_id || user?.id;
  const isMeOnline = onlineUsers.some((u) => u.user_id === myId);

  const isActive = (href) => location.pathname === href || location.pathname.startsWith(href + '/');
  const isPartsCatalogueActive = () => isActive('/parts') || isActive('/taxonomy');

  const handleLogout = () => {
    logout();
  };

  const getRoleBadgeColor = (role) => {
    const colors = {
      admin: 'bg-red-500/20 text-red-300',
      designs_manager: 'bg-purple-500/20 text-purple-300',
      designs_engineer: 'bg-blue-500/20 text-blue-300',
      quality_manager: 'bg-emerald-500/20 text-emerald-300',
      quality_engineer: 'bg-teal-500/20 text-teal-300',
      inventory_manager: 'bg-amber-500/20 text-amber-300',
      operator: 'bg-orange-500/20 text-orange-300',
      viewer: 'bg-slate-500/20 text-slate-300',
    };
    return colors[role] || colors.viewer;
  };

  return (
    <div className="h-screen flex flex-col overflow-hidden bg-slate-50">
      {/* Header with HADEN ATLAS Branding - Enhanced height for prominence */}
      <header className="bg-[#101957] text-white shadow-lg z-50 flex-shrink-0">
        <div className="max-w-full mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-20">
            {/* Logo - HADEN ATLAS Branding - More Prominent */}
            <Link to="/" className="flex items-center py-2">
              <HadenAtlasLogo 
                size="default" 
                variant="light"
                showSubtitle={false}
              />
            </Link>

            {/* Desktop Navigation */}
            <nav className="hidden md:flex items-center space-x-1">
              {/* Parts Library Dropdown */}
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button
                    className={`flex items-center space-x-2 px-4 py-2.5 rounded-lg text-sm font-medium transition-colors ${
                      isPartsCatalogueActive()
                        ? 'bg-[#C8B59A] text-[#101957]'
                        : 'text-slate-300 hover:bg-white/10 hover:text-white'
                    }`}
                    data-testid="parts-catalogue-dropdown"
                  >
                    <Package className="w-4 h-4" />
                    <span>Parts Library</span>
                    <ChevronDown className="w-3 h-3" />
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="start" className="w-48 bg-[#101957] border-slate-700 text-white">
                  <DropdownMenuItem asChild>
                    <Link
                      to="/parts"
                      className={`flex items-center space-x-2 cursor-pointer ${
                        isActive('/parts') && !isActive('/taxonomy') ? 'bg-white/10' : 'hover:bg-white/10'
                      }`}
                      data-testid="parts-list-link"
                    >
                      <List className="w-4 h-4" />
                      <span>Parts List</span>
                    </Link>
                  </DropdownMenuItem>
                  <DropdownMenuItem asChild>
                    <Link
                      to="/taxonomy"
                      className={`flex items-center space-x-2 cursor-pointer ${
                        isActive('/taxonomy') ? 'bg-white/10' : 'hover:bg-white/10'
                      }`}
                      data-testid="taxonomy-link"
                    >
                      <Tag className="w-4 h-4" />
                      <span>Taxonomy</span>
                    </Link>
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>

              {/* Products Library Tab */}
              <Link
                to="/product-library"
                className={`flex items-center space-x-2 px-4 py-2.5 rounded-lg text-sm font-medium transition-colors ${
                  isActive('/product-library') || isActive('/bom')
                    ? 'bg-[#C8B59A] text-[#101957]'
                    : 'text-slate-300 hover:bg-white/10 hover:text-white'
                }`}
                data-testid="bom-nav-link"
              >
                <Layers className="w-4 h-4" />
                <span>Products Library</span>
              </Link>

              {/* Inventory Tab */}
              <Link
                to="/inventory"
                className={`flex items-center space-x-2 px-4 py-2.5 rounded-lg text-sm font-medium transition-colors ${
                  isActive('/inventory')
                    ? 'bg-[#C8B59A] text-[#101957]'
                    : 'text-slate-300 hover:bg-white/10 hover:text-white'
                }`}
                data-testid="inventory-nav-link"
              >
                <Boxes className="w-4 h-4" />
                <span>Inventory</span>
              </Link>
              
              {/* Admin Dropdown - Only visible to admin users */}
              {isAuthenticated && user?.role === 'admin' && (
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <button
                      className={`flex items-center space-x-2 px-4 py-2.5 rounded-lg text-sm font-medium transition-colors ${
                        isActive('/admin') || isActive('/settings') || isActive('/db-explorer')
                          ? 'bg-[#C8B59A] text-[#101957]'
                          : 'text-slate-300 hover:bg-white/10 hover:text-white'
                      }`}
                      data-testid="admin-dropdown-trigger"
                    >
                      <Shield className="w-4 h-4" />
                      <span>Admin</span>
                      <ChevronDown className="w-3 h-3" />
                    </button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="w-52 bg-[#101957] border-slate-700 text-white">
                    <DropdownMenuLabel className="text-slate-400 text-xs uppercase tracking-wider">
                      User Management
                    </DropdownMenuLabel>
                    <DropdownMenuItem asChild>
                      <Link
                        to="/admin/user"
                        className={`flex items-center space-x-2 cursor-pointer ${
                          isActive('/admin/user') ? 'bg-white/10' : 'hover:bg-white/10'
                        }`}
                        data-testid="users-link"
                      >
                        <Users className="w-4 h-4" />
                        <span>Users</span>
                      </Link>
                    </DropdownMenuItem>
                    <DropdownMenuItem asChild>
                      <Link
                        to="/admin/permissions"
                        className={`flex items-center space-x-2 cursor-pointer ${
                          isActive('/admin/permissions') ? 'bg-white/10' : 'hover:bg-white/10'
                        }`}
                        data-testid="permissions-link"
                      >
                        <Shield className="w-4 h-4" />
                        <span>Role Permissions</span>
                      </Link>
                    </DropdownMenuItem>
                    <DropdownMenuSeparator className="bg-slate-700" />
                    <DropdownMenuLabel className="text-slate-400 text-xs uppercase tracking-wider">
                      System
                    </DropdownMenuLabel>
                    <DropdownMenuItem asChild>
                      <Link
                        to="/db-explorer"
                        className={`flex items-center space-x-2 cursor-pointer ${
                          isActive('/db-explorer') ? 'bg-white/10' : 'hover:bg-white/10'
                        }`}
                        data-testid="db-explorer-link"
                      >
                        <Database className="w-4 h-4" />
                        <span>DB Collections</span>
                      </Link>
                    </DropdownMenuItem>
                    <DropdownMenuItem asChild>
                      <Link
                        to="/settings"
                        className={`flex items-center space-x-2 cursor-pointer ${
                          location.pathname === '/settings' ? 'bg-white/10' : 'hover:bg-white/10'
                        }`}
                        data-testid="settings-link"
                      >
                        <Settings className="w-4 h-4" />
                        <span>Settings</span>
                      </Link>
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              )}
            </nav>

            {/* User Menu */}
            <div className="hidden md:flex items-center space-x-4">
              {/* Who's Online - Real-time presence indicator */}
              {isAuthenticated && (
                <WhosOnline />
              )}
              
              {/* Separator */}
              {isAuthenticated && user && (
                <div className="w-px h-8 bg-slate-600" />
              )}
              
              {isAuthenticated && user && (
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button
                      variant="ghost"
                      className="flex items-center space-x-2 text-slate-300 hover:text-white hover:bg-white/10"
                      data-testid="user-menu-trigger"
                    >
                      <div className="relative">
                        <div className="w-9 h-9 bg-[#C8B59A] rounded-full flex items-center justify-center">
                          <User className="w-5 h-5 text-[#101957]" />
                        </div>
                        <span
                          className={`absolute -bottom-0.5 -right-0.5 w-2.5 h-2.5 rounded-full border-2 border-[#101957] ${isMeOnline ? 'bg-emerald-500' : 'bg-slate-500'}`}
                          data-testid="self-presence-dot"
                        />
                      </div>
                      <div className="text-left">
                        <p className="text-sm font-medium">{user.display_name || user.full_name}</p>
                        <p className={`text-xs px-1.5 py-0.5 rounded ${getRoleBadgeColor(user.role)}`}>
                          {user.role?.replace('_', ' ')}
                        </p>
                      </div>
                      <ChevronDown className="w-4 h-4" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="w-56 bg-[#101957] border-slate-700 text-white">
                    <DropdownMenuLabel className="text-slate-400">
                      {user.email}
                    </DropdownMenuLabel>
                    <DropdownMenuSeparator className="bg-slate-700" />
                    <DropdownMenuItem asChild>
                      <Link
                        to="/settings/profile"
                        className="flex items-center space-x-2 cursor-pointer hover:bg-white/10"
                        data-testid="user-settings-link"
                      >
                        <Settings className="w-4 h-4" />
                        <span>Settings</span>
                      </Link>
                    </DropdownMenuItem>
                    <DropdownMenuSeparator className="bg-slate-700" />
                    <DropdownMenuItem
                      onClick={handleLogout}
                      className="text-red-400 hover:text-red-300 hover:bg-white/10 cursor-pointer"
                      data-testid="logout-button"
                    >
                      <LogOut className="w-4 h-4 mr-2" />
                      Sign Out
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              )}
            </div>

            {/* Mobile menu button */}
            <button
              className="md:hidden p-2 rounded-lg hover:bg-white/10"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              data-testid="mobile-menu-button"
            >
              {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
        </div>

        {/* Mobile Navigation */}
        {mobileMenuOpen && (
          <div className="md:hidden border-t border-white/10">
            <nav className="px-4 py-2 space-y-1">
              {/* Parts Library Section */}
              <div className="px-4 py-1 text-xs text-slate-400 uppercase tracking-wider">
                Parts Library
              </div>
              <Link
                to="/parts"
                className={`flex items-center space-x-2 px-4 py-3 rounded-lg text-sm font-medium ${
                  isActive('/parts') && !isActive('/taxonomy')
                    ? 'bg-[#C8B59A] text-[#101957]'
                    : 'text-slate-300 hover:bg-white/10'
                }`}
                onClick={() => setMobileMenuOpen(false)}
              >
                <List className="w-5 h-5" />
                <span>Parts List</span>
              </Link>
              <Link
                to="/taxonomy"
                className={`flex items-center space-x-2 px-4 py-3 rounded-lg text-sm font-medium ${
                  isActive('/taxonomy')
                    ? 'bg-[#C8B59A] text-[#101957]'
                    : 'text-slate-300 hover:bg-white/10'
                }`}
                onClick={() => setMobileMenuOpen(false)}
              >
                <Tag className="w-5 h-5" />
                <span>Taxonomy</span>
              </Link>

              {/* Inventory */}
              <Link
                to="/inventory"
                className={`flex items-center space-x-2 px-4 py-3 rounded-lg text-sm font-medium ${
                  isActive('/inventory')
                    ? 'bg-[#C8B59A] text-[#101957]'
                    : 'text-slate-300 hover:bg-white/10'
                }`}
                onClick={() => setMobileMenuOpen(false)}
              >
                <Boxes className="w-5 h-5" />
                <span>Inventory</span>
              </Link>
              
              {/* Admin links for mobile - Only visible to admin users */}
              {isAuthenticated && user?.role === 'admin' && (
                <>
                  <div className="border-t border-white/10 my-2 pt-2">
                    <div className="px-4 py-1 text-xs text-slate-400 uppercase tracking-wider">
                      User Management
                    </div>
                    <Link
                      to="/admin/user"
                      className={`flex items-center space-x-2 px-4 py-3 rounded-lg text-sm font-medium ${
                        isActive('/admin/user')
                          ? 'bg-[#C8B59A] text-[#101957]'
                          : 'text-slate-300 hover:bg-white/10'
                      }`}
                      onClick={() => setMobileMenuOpen(false)}
                      data-testid="mobile-users-link"
                    >
                      <Users className="w-5 h-5" />
                      <span>Users</span>
                    </Link>
                    <Link
                      to="/admin/permissions"
                      className={`flex items-center space-x-2 px-4 py-3 rounded-lg text-sm font-medium ${
                        isActive('/admin/permissions')
                          ? 'bg-[#C8B59A] text-[#101957]'
                          : 'text-slate-300 hover:bg-white/10'
                      }`}
                      onClick={() => setMobileMenuOpen(false)}
                      data-testid="mobile-permissions-link"
                    >
                      <Shield className="w-5 h-5" />
                      <span>Role Permissions</span>
                    </Link>
                    <div className="px-4 py-1 text-xs text-slate-400 uppercase tracking-wider mt-2">
                      System
                    </div>
                    <Link
                      to="/db-explorer"
                      className={`flex items-center space-x-2 px-4 py-3 rounded-lg text-sm font-medium ${
                        isActive('/db-explorer')
                          ? 'bg-[#C8B59A] text-[#101957]'
                          : 'text-slate-300 hover:bg-white/10'
                      }`}
                      onClick={() => setMobileMenuOpen(false)}
                      data-testid="mobile-db-explorer-link"
                    >
                      <Database className="w-5 h-5" />
                      <span>DB Collections</span>
                    </Link>
                    <Link
                      to="/settings"
                      className={`flex items-center space-x-2 px-4 py-3 rounded-lg text-sm font-medium ${
                        location.pathname === '/settings'
                          ? 'bg-[#C8B59A] text-[#101957]'
                          : 'text-slate-300 hover:bg-white/10'
                      }`}
                      onClick={() => setMobileMenuOpen(false)}
                      data-testid="mobile-settings-link"
                    >
                      <Settings className="w-5 h-5" />
                      <span>Settings</span>
                    </Link>
                  </div>
                </>
              )}
              
              {isAuthenticated && user && (
                <>
                  <div className="border-t border-white/10 my-2 pt-2">
                    <div className="px-4 py-2 text-sm text-slate-400">
                      Signed in as <span className="text-white">{user.full_name}</span>
                    </div>
                    <Link
                      to="/settings/profile"
                      className="flex items-center space-x-2 w-full px-4 py-3 rounded-lg text-sm font-medium text-slate-300 hover:bg-white/10"
                      onClick={() => setMobileMenuOpen(false)}
                      data-testid="mobile-user-settings-link"
                    >
                      <Settings className="w-5 h-5" />
                      <span>Settings</span>
                    </Link>
                    <button
                      onClick={handleLogout}
                      className="flex items-center space-x-2 w-full px-4 py-3 rounded-lg text-sm font-medium text-red-400 hover:bg-white/10"
                      data-testid="mobile-logout-button"
                    >
                      <LogOut className="w-5 h-5" />
                      <span>Sign Out</span>
                    </button>
                  </div>
                </>
              )}
            </nav>
          </div>
        )}
      </header>

      {/* Same-page awareness strip — only visible when others are on this page */}
      {isAuthenticated && <PageViewers />}

      {/* Main Content - Takes remaining height, no page scroll */}
      <main className="flex-1 overflow-y-auto relative">
        {children}
      </main>
    </div>
  );
};

export default Layout;
